import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import PageContainer from '../../shared/pageContainer';
import DashboardSection from '../../shared/dashboardSection';
import CustomButton from '../../shared/customButton';
import CustomSwitch from '../../shared/customSwitch';
import ActionButton from '../../shared/actionButton';
import CourseMiniCard from '../../shared/courseMiniCard';
import OneOnOneMiniCard from '../../shared/oneOnOneMiniCard';
import TaskCard from '../../shared/taskCard';
import TeamUserStatsCard from '../../shared/teamUserStatsCard';
import PlanMiniCard from '../../shared/planMiniCard';
import ToDoCard from '../../shared/toDoCard';
import GlobalNotifications from '../../shared/globalNotifications';
import AlertDialog from '../../shared/alertDialog';
import ManageActionPlan from '../../shared/manageActionPlan';
import CustomFormDrawer from '../../shared/customFormDrawer';
import ManageOneOnOne from '../../shared/manageOneOnOne';
import DotsMenu from '../../shared/dotsMenu';
import PreviewTeamDialog from '../../shared/previewTeamDialog';
import { ReactComponent as CleanIcon } from '../../../assets/icons/cleaning.svg';
import { ReactComponent as TasksIcon } from '../../../assets/icons/format_list_bulleted.svg';
import { ReactComponent as CoursesIcon } from '../../../assets/icons/school-dark.svg';
import { ReactComponent as PlansIcon } from '../../../assets/icons/assignment_darker.svg';
import { ReactComponent as OneOnOneIcon } from '../../../assets/icons/people-dark.svg';
import { ReactComponent as MyTeamIcon } from '../../../assets/icons/groups.svg';
import { ReactComponent as RequestIcon } from '../../../assets/icons/back_hand.svg';
import { PERMISSION_CODES } from '../../../constants/permissions';
import {
  API_ONE_ON_ONE_ME,
  API_ONE_ON_ONE_USER_REQUEST,
  API_ONE_ON_ONE_TOPICS,
  API_USERS_BASIC,
  api_get_survey_tracking_by_id,
  api_course_users,
  api_one_on_one_topic,
  api_action_plan,
  api_one_on_one_record,
  api_absence_request_status,
} from '../../../constants/apiRoutes';
import {
  ABSENCE_REQUEST_STATUS_TYPES,
  USER_STATUSES,
} from '../../../constants/statuses';
import {
  NOTIFICATION_ACTIONS,
  NOTIFICATION_MODEL_KEYS,
} from '../../../constants/globalNotifications';
import { INITIAL_TOPIC_DATA } from '../../../constants/oneOnOne';
import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';
import { FEATURE_COLORS } from '../../../constants/appConfig';
import { PRESETS } from '../../../constants/presets';
import { SYSTEM_ROLES } from '../../../constants/roles';
import { APP_PAGES, PARAMS } from '../../../constants/pages';
import { GENERAL_CATEGORY_ID } from '../../../constants/tags';
import {
  PEOPLE_PARAM_SCOPE_VALUES,
  PEOPLE_DEFAULT_ORDERING,
} from '../../../constants/people';
import {
  DASHBOARD_DEFAULT_PARAMS,
  INITIAL_TEAM_DATA,
  DASHBOARD_1_1_TABS,
  DASHBOARD_PLAN_TABS,
  DASHBOARD_TASKS_PRESET,
  MANAGE_TEAM_FIELDS,
} from '../../../constants/dashboard';
import { dashboardSelector } from '../../../store/selectors/dashboardSelector';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { coursesSelector } from '../../../store/selectors/coursesSelector';
import { oneOnOneTopicsSelector } from '../../../store/selectors/oneOnOneTopicsSelector';
import { notificationsSelector } from '../../../store/selectors/notificationsSelector';
import {
  getOneOnOne,
  clearOneOnOne,
  getMyOneOnOneRequest,
  clearMyOneOnOneRequest,
  getPlans,
  clearPlans,
  getMyCourses,
  clearMyCourses,
  getMyTasks,
  clearMyTasks,
  getMyToDo,
  getTeamStats,
  clearMyToDo,
  clearTeamStats,
  clearAllLoaders,
  setLoaders,
  changeTaskStatus,
} from '../../../store/modules/dashboard';
import {
  getOneOnOneTopics,
  clearOneOnOneTopics,
} from '../../../store/modules/oneOnOneTopics';
import {
  getTagsCategories,
  clearTagsCategories,
  addMultipleTags,
} from '../../../store/modules/tags';
import { setIsInlineSurvey } from '../../../store/modules/fillSurvey';
import { getCourses, clearCourses } from '../../../store/modules/courses';
import {
  addNewPreset,
  onEditPreset,
  deleteSelectedPreset,
} from '../../../store/modules/organizationSettings';
import {
  getCourseEnrolledUsers,
  clearCourseEnrolledUsers,
} from '../../../store/modules/courseDetails';
import {
  getNotifications,
  getNotificationsCount,
  clearNotifications,
  clearNotificationItems,
  onClearNotification,
  onClearAllNotifications,
} from '../../../store/modules/notifications';
import { useTranslations } from '../../../utility/useTranslations';
import { onSaveCreatableTag } from '../../../utility/tagUtils';
import {
  isArrayEmpty,
  trimString,
  isObject,
  isObjectEmpty,
  getItemById,
  getObjectToNumberArray,
  getItemIndex,
  getUnionOfTwoArrays,
} from '../../../utility/helpers';
import {
  hasAuthPermissionsCheck,
  hasScopedAuthPermissionCheck,
  prepareUsersWithGrantedPermission,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import { hasNextPage, showSuccessMessage } from '../../../utility/uiUtils';
import {
  prepareCourses,
  prepareEnrollmentStatuses,
} from '../../../utility/courseUtils';
import http from '../../../utility/http';
import {
  prepareOneOnOneRecordsData,
  prepareUserTopicData,
  prepareUserTopicSidebarTranslations,
  prepareUserTopicFields,
} from '../../../utility/oneOnOne';
import { prepareSurveyTypes } from '../../../utility/survey';
import {
  preparePlansData,
  prepareTranslatedActionPlanStatuses,
} from '../../../utility/actionPlans';
import {
  prepareToDoItems,
  prepareMyTeamMenuItems,
  prepareMyTeams,
  prepareOneOnOneTabs,
  prepareActionPlanTabs,
  prepareDashboardPresets,
} from '../../../utility/dashboard';

const useStyles = makeStyles(
  ({ palette: { primary }, breakpoints, spacing }) => ({
    root: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
      gridTemplateRows: 'repeat(3, 260px) minMax(362px, min-content)',
      gridGap: 24,
      paddingTop: spacing(8),
      [breakpoints.up('xLg')]: {
        gridGap: 32,
      },
    },
    twoColumns: {
      gridColumn: 'span 2',
    },
    fullRow: {
      gridColumn: 'span 3',
    },
    tasksSection: {
      gridRow: 'span 2;',
    },
    taskSectionOneRow: {
      gridRow: 'span 1;',
    },
    noDataIcon: {
      height: 22,
      width: 'auto',
    },
    switch: {
      width: 'auto',
    },
    myTeamHeader: {
      marginBottom: 0,
    },
    myTeamContent: {
      height: 'calc(100% - 56px - 48px)',
    },
    scrollWrapperClass: {
      paddingTop: spacing(2),
    },
    myTeam: {
      height: 422,
    },
    myTeamNoCourses: {
      height: 378,
    },
    myTeamScrollArrow: {
      top: 136,
      [breakpoints.up('xLg')]: {
        top: 133,
      },
    },
    myTeamScrollArrowNoCourses: {
      top: 114,
      [breakpoints.up('xLg')]: {
        top: 110,
      },
    },
    myTeamNoResults: {
      top: 'calc(50% - 16px)', // middle of content - 1/2 header margin
    },
    actionButton: {
      fontFamily: 'ProximaNova-Bold',
      fontSize: 14,
      lineHeight: '24px',
      padding: spacing(1, 4),
      maxHeight: 32,
      marginLeft: spacing(2),
      '&:disabled': {
        backgroundColor: primary.white,
        border: `1px solid ${primary.bluish7}`,
        color: primary.bluish7,
      },
    },
    actionButtonIcon: {
      marginLeft: 0,
      marginTop: spacing(-0.5),
    },
    actionButtonIconDisabled: {
      '& svg path': {
        fill: primary.bluish7,
      },
    },
    menu: {
      marginLeft: spacing(2),
    },
    addTeamButton: {
      marginLeft: spacing(8),
    },
    addTeamIconButton: {
      backgroundColor: primary.white,
      '&:hover': {
        backgroundColor: primary.white,
      },
    },
    drawerContent: {
      height: '100%',
    },
  })
);

const DashboardPage = ({
  navigate,
  auth,
  accessControl,
  organizationSettings,
  presets = {},
  dispatch,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.DASHBOARD);
  const location = useLocation();
  const shouldRefresh = location?.state?.shouldRefresh;

  const {
    myToDo,
    myTasks,
    myCourses,
    plans,
    myOnOneRequest,
    oneOnOne,
    teamStats,
    loadings,
  } = useSelector(dashboardSelector);
  const { categories } = useSelector(tagsSelector);
  const { courses } = useSelector(coursesSelector);
  const { topics } = useSelector(oneOnOneTopicsSelector);
  const { count: notificationCount, items: notificationItems } = useSelector(
    notificationsSelector
  );

  const [myActionPlansTab] = DASHBOARD_PLAN_TABS;
  const [myOneOnOneTab] = DASHBOARD_1_1_TABS;

  const actionPlanTabs = prepareActionPlanTabs(
    translations.plans.tabs,
    accessControl
  );
  const oneOnOneTabs = prepareOneOnOneTabs(
    translations.oneOnOne.tabs,
    accessControl
  );

  const { teamPresets, tasksPreset } = prepareDashboardPresets(presets);

  const [isIntialLoad, setIsInitialLoad] = useState(true);
  const [myCoursesPage, setMyCoursesPage] = useState(1);
  const [plansPage, setPlansPage] = useState(1);
  const [oneOnOnePage, setOneOnOnePage] = useState(1);
  const [hasUpcomingOneOnOne, setHasUpcomingOneOnOne] = useState(false);
  const [isEmployeeTopicOpened, setIsEmployeeTopicOpened] = useState(false);
  const [employeeTopic, setEmployeeTopic] = useState(INITIAL_TOPIC_DATA);
  const [isDeleteRequestOpened, setIsDeleteRequestOpened] = useState(false);
  const [isDismissRequestOpened, setIsDismissRequestOpened] = useState(false);
  const [oneOnOneRequest, setOneOnOneRequest] = useState({});
  const [isAcceptRequestOpened, setIsAcceptRequestOpened] = useState(false);
  const [activeOneOnOneTab, setActiveOneOnOneTab] = useState(oneOnOneTabs[0]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [managableUsers, setManagableUsers] = useState([]);
  const [myTasksPage, setMyTasksPage] = useState(1);
  const [selectedTask, setSelectedTask] = useState(null);
  const [hasTaskOnLoad, setHasTasksOnLoad] = useState(false);
  const [isTasksVisibilittLoading, setIsTasksVisibilityLoading] =
    useState(false);
  const [shouldHideFinishedTask, setShouldHideFinishedTasks] = useState(
    !!tasksPreset?.data?.shouldHideFinishedTask
  );
  const [isPlanProposalOpened, setIsPlanProposalOpened] = useState(false);
  const [isDismissProposalOpened, setIsDismissProposalOpened] = useState(false);
  const [planProposal, setPlanProposal] = useState({});
  const [isAcceptAbsenceRequestOpened, setIsAcceptAbsenceRequestOpened] =
    useState(false);
  const [isRejectAbsenceRequestOpened, setIsRejectAbsenceRequestOpened] =
    useState(false);
  const [absenceRequestId, setAbsenceRequestId] = useState(null);
  const [isPlanProposal, setIsPlanProposa] = useState(false);
  const [activePlansTab, setActivePlansTab] = useState(actionPlanTabs[0]);
  const [activeTeam, setActiveTeam] = useState(null);
  const [team, setTeam] = useState(INITIAL_TEAM_DATA);
  const [isManageTeamOpened, setIsManageTeamOpened] = useState(false);
  const [myDirectReporters, setMyDirectReporters] = useState([]);
  const [isDeleteTeamOpened, setIsDeleteTeamOpened] = useState(false);
  const [isViewTeamOpened, setIsViewTeamOpened] = useState(false);
  const [mySuperior, setMySuperior] = useState(null);
  const [teamsAvailableUsers, setTeamsAvailableUsers] = useState([]);

  const [
    canViewActionPlans,
    canEditActionPlans,
    canViewOneOnOne,
    canViewCourses,
  ] = hasAuthPermissionsCheck(
    [
      PERMISSION_CODES.PLANS_VIEW,
      PERMISSION_CODES.PLANS_EDIT,
      PERMISSION_CODES.ONE_ON_ONE_VIEW,
      PERMISSION_CODES.LEARNING_VIEW,
    ],
    accessControl
  );
  const { users_see_courses } = organizationSettings;
  const {
    myToDoLoader,
    myTasksLoader,
    myCoursesLoader,
    plansLoader,
    oneOnOneLoader,
    teamStatsLoader,
  } = loadings;
  const hasCourses = !isArrayEmpty(myCourses.results);
  const hasOneOnOneRequest = !oneOnOneLoader && !isObjectEmpty(myOnOneRequest);
  const hasActionPlanProposeButton =
    !plansLoader && activePlansTab.id === myActionPlansTab.id;

  const hasOneOnOneRequestButton =
    !oneOnOneLoader && activeOneOnOneTab.id === myOneOnOneTab.id && mySuperior;
  const planStatuses = prepareTranslatedActionPlanStatuses(
    translations.plans.plan.statuses
  );
  const courseStatuses = prepareEnrollmentStatuses(
    translations.courses.statuses
  );
  const surveyTypes = prepareSurveyTypes(translations.toDo.survey);
  const teams = useMemo(
    () =>
      prepareMyTeams(
        translations.myTeam.title,
        myDirectReporters,
        teamPresets,
        teamsAvailableUsers
      ),
    [translations, myDirectReporters, teamPresets, teamsAvailableUsers]
  );

  const getAvailableUsers = async () => {
    const { data } = await http.get(API_USERS_BASIC, {
      params: {
        ...PEOPLE_DEFAULT_ORDERING,
        [PARAMS.EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
        [PARAMS.EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
      },
    });

    setAvailableUsers(data);
    setManagableUsers(
      prepareUsersWithGrantedPermission(
        data,
        PERMISSION_CODES.PLANS_EDIT,
        accessControl,
        auth.id
      )
    );
  };

  const getTeamsAvailableUsers = async () => {
    const { data } = await http.get(API_USERS_BASIC, {
      params: {
        ...PEOPLE_DEFAULT_ORDERING,
        [PARAMS.EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
        [PARAMS.EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
        [PARAMS.RBAC_SCOPE_PERMISSION]: PERMISSION_CODES.PROFILES_VIEW,
        [PARAMS.EXCLUDE]: auth.id,
      },
    });

    setTeamsAvailableUsers(data);
    return data;
  };

  const getMySuperior = async () => {
    const { data } = await http.get(API_USERS_BASIC, {
      params: {
        ...PEOPLE_DEFAULT_ORDERING,
        [PARAMS.EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
        [PARAMS.EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
        [PARAMS.ID]: auth.report_to,
      },
    });
    const [superior] = data;
    setMySuperior(superior);
  };

  const getMyDirectReporters = async () => {
    const { data } = await http.get(API_USERS_BASIC, {
      params: {
        [PARAMS.EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
        [PARAMS.EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
        [PARAMS.SCOPE]: PEOPLE_PARAM_SCOPE_VALUES.DIRECT_REPORTS,
      },
    });
    setMyDirectReporters(data);
    return data;
  };

  const canAccessUserProfileCheck = userId =>
    isUserProfileAccessibleCheck(accessControl, auth.id, userId);

  const canAccessDetailsCheck = (userId, requiredPermission) =>
    hasScopedAuthPermissionCheck(
      requiredPermission,
      accessControl,
      auth.id,
      userId
    );

  const getDashboardData = useCallback(async () => {
    try {
      getNotificationsCount(dispatch);
      getMyTasks(dispatch, {
        ...(shouldHideFinishedTask ? { is_finished: false } : {}),
      }).then(data => setHasTasksOnLoad(!isArrayEmpty(data.results)));
      getMyToDo(dispatch);

      const directReporters = await getMyDirectReporters();
      const teamsUsers = await getTeamsAvailableUsers();
      const hasReporters = !isArrayEmpty(directReporters);
      const availableTeams = prepareMyTeams(
        translations.myTeam.title,
        directReporters,
        teamPresets,
        teamsUsers
      );
      const [currentTeam] = availableTeams;

      if (!isArrayEmpty(availableTeams)) {
        await getTeamStats(dispatch, getObjectToNumberArray(currentTeam.users));
      } else {
        dispatch(setLoaders({ teamStatsLoader: false }));
      }

      if (!hasReporters && currentTeam) {
        setActiveTeam({
          id: currentTeam.id,
          name: currentTeam.name,
          location: currentTeam.location,
          count: currentTeam.users.length,
          users: currentTeam.users,
          isEditable: true,
        });
      }

      if (hasReporters && !isArrayEmpty(availableTeams)) {
        setActiveTeam({
          id: 'my-team',
          name: translations.myTeam.title,
          count: directReporters.length,
          users: directReporters,
          isEditable: false,
        });
      }

      if (canViewActionPlans || canViewOneOnOne) {
        if (canViewActionPlans) {
          if (canEditActionPlans) {
            getAvailableUsers();
          }
          getPlans(dispatch, activePlansTab.url);
        }

        if (canViewOneOnOne) {
          await Promise.all([
            getMyOneOnOneRequest(dispatch),
            getOneOnOneTopics(dispatch),
            getOneOnOne(dispatch, activeOneOnOneTab.url),
            ...(!auth.report_to_organization ? [getMySuperior()] : []),
          ]).finally(() => dispatch(setLoaders({ oneOnOneLoader: false })));
        } else {
          dispatch(setLoaders({ oneOnOneLoader: false }));
        }
        getTagsCategories(dispatch);
      }
      if (!canViewActionPlans) {
        dispatch(setLoaders({ plansLoader: false }));
      }
      if (!canViewOneOnOne) {
        dispatch(setLoaders({ oneOnOneLoader: false }));
      }
      if (users_see_courses && canViewCourses) {
        getMyCourses(dispatch);
      } else {
        dispatch(setLoaders({ myCoursesLoader: false }));
      }
    } finally {
      setIsInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, auth, users_see_courses]);

  const cleanup = useCallback(() => {
    dispatch(clearPlans());
    dispatch(clearMyCourses());
    dispatch(clearOneOnOne());
    dispatch(clearMyToDo());
    dispatch(clearTeamStats());
    dispatch(clearAllLoaders());
    dispatch(clearMyOneOnOneRequest());
    dispatch(clearMyTasks());
    dispatch(clearTagsCategories());
    dispatch(clearCourses());
    dispatch(clearCourseEnrolledUsers());
    dispatch(clearNotifications());
    clearOneOnOneTopics(dispatch);
  }, [dispatch]);

  useEffect(() => {
    getDashboardData();
  }, [getDashboardData, shouldRefresh]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const onGoToUserProfile = (userId, section) =>
    navigate(`/people/${userId}${section ? '/' : ''}${section || ''}`);

  const onViewCourseDetails = courseId => navigate(`/learning/${courseId}`);

  const canAccessNotificationCheck = notification => {
    const { model, user } = notification;
    const userId = user?.id;
    const { ACTION_PLAN, ONE_ON_ONE } = NOTIFICATION_MODEL_KEYS;

    if (model === ACTION_PLAN) {
      return canAccessDetailsCheck(userId, PERMISSION_CODES.PLANS_VIEW);
    }
    if (model === ONE_ON_ONE) {
      return canAccessDetailsCheck(userId, PERMISSION_CODES.ONE_ON_ONE_VIEW);
    }

    return canAccessUserProfileCheck(userId);
  };

  const onLoadMoreCourses = () => {
    const page = myCoursesPage + 1;

    setMyCoursesPage(page);
    return getMyCourses(dispatch, { page }, true);
  };

  const onGetCourses = params => getCourses(dispatch, params);

  const onGetCourseEnrolledUsers = (courseId, params) =>
    getCourseEnrolledUsers(dispatch, courseId, params);

  const onCoursesSearch = value => {
    const text = trimString(value);
    const params = {
      ...(text ? { search: text } : {}),
    };

    return onGetCourses(params);
  };

  const onCourseStatusChange = (courseId, statusId) => {
    return http
      .patch(`${api_course_users(courseId)}${auth.id}/`, {
        status: statusId,
      })
      .then(() => showSuccessMessage(translations.courses.successStatus))
      .finally(() => {
        setMyCoursesPage(DASHBOARD_DEFAULT_PARAMS.page);
        getMyCourses(dispatch, { page: DASHBOARD_DEFAULT_PARAMS.page });
      });
  };

  const onViewOneOnOneDetails = oneOnOneId =>
    navigate(`/1-1/${oneOnOneId}/details`);

  const onLoadMoreOneOnOne = () => {
    const page = oneOnOnePage + 1;

    setOneOnOnePage(page);
    return getOneOnOne(dispatch, activeOneOnOneTab.url, { page }, true);
  };

  const onOpenOneOnOneRequest = () => {
    setIsEmployeeTopicOpened(true);
    setEmployeeTopic(
      prepareUserTopicData(
        { moderator: mySuperior, ...myOnOneRequest },
        auth.id
      )
    );
  };

  const onCloseOneOnOneRequest = () => {
    setIsEmployeeTopicOpened(false);
    setEmployeeTopic(INITIAL_TOPIC_DATA);
  };

  const onToggleUpcomingOneOnOneDialog = () =>
    setHasUpcomingOneOnOne(prevUpcoming => !prevUpcoming);

  const onConfirmUpcomingMeetingDialog = () => {
    onToggleUpcomingOneOnOneDialog();
    onOpenOneOnOneRequest();
  };

  const onAddOneOnOneRequest = () => {
    return http
      .get(API_ONE_ON_ONE_ME, {
        params: { completion_status: 'upcoming' },
      })
      .then(({ data }) => {
        const hasUpcoming = data.results.some(
          m => m.moderator?.id === mySuperior?.id
        );

        return hasUpcoming
          ? onToggleUpcomingOneOnOneDialog()
          : onOpenOneOnOneRequest();
      });
  };

  const onSaveOneOnOneRequest = topic => {
    if (topic.id) {
      return http
        .patch(api_one_on_one_topic(topic.id), {
          name: topic.name,
        })
        .then(() => {
          getMyOneOnOneRequest(dispatch);
          showSuccessMessage(
            translations.oneOnOne.successMessages.meetingRequestUpdated
          );
        });
    }

    return http
      .post(API_ONE_ON_ONE_TOPICS, { name: topic.name })
      .then(({ data }) => {
        return http
          .post(API_ONE_ON_ONE_USER_REQUEST, {
            agenda: [data.id],
          })
          .then(() => {
            setOneOnOnePage(DASHBOARD_DEFAULT_PARAMS.page);
            showSuccessMessage(
              translations.oneOnOne.successMessages.meetingRequestAdded
            );
            getMyOneOnOneRequest(dispatch);
            getOneOnOne(dispatch);
          });
      });
  };

  const onDeleteRequestOpen = () => {
    setEmployeeTopic(
      prepareUserTopicData(
        { moderator: mySuperior, ...myOnOneRequest },
        auth.id
      )
    );
    setIsDeleteRequestOpened(true);
  };

  const onCloseDeleteRequestDialog = () => setIsDeleteRequestOpened(false);

  const onDeleteRequest = async () => {
    try {
      await http.delete(API_ONE_ON_ONE_USER_REQUEST);
      onCloseDeleteRequestDialog();
      dispatch(clearMyOneOnOneRequest());
      showSuccessMessage(
        translations.oneOnOne.deleteMeetingRequestDialog.deleteSuccess
      );
    } catch {
      onCloseDeleteRequestDialog();
    }
  };

  const onOpenDismissRequest = requestId => {
    setOneOnOneRequest(requestId);
    setIsDismissRequestOpened(true);
  };

  const onCloseDismissRequest = () => {
    setIsDismissRequestOpened(false);
    setOneOnOneRequest({});
  };

  const onDismissRequest = async () => {
    try {
      await http.delete(api_one_on_one_record(oneOnOneRequest));
      onCloseDismissRequest();
      showSuccessMessage(
        translations.oneOnOne.dismissOneOnOneRequestDialog.dismissSuccess
      );
      getMyToDo(dispatch);
    } catch {
      onCloseDismissRequest();
      setPlanProposal({});
    }
  };

  const onAcceptRequest = async requestId => {
    const { data } = await http.get(api_one_on_one_record(requestId));

    setIsAcceptRequestOpened(true);
    setOneOnOneRequest(data);
  };

  const onCloseRequest = () => {
    setIsAcceptRequestOpened(false);
    setOneOnOneRequest({});
  };

  const onSaveAcceptedRequest = async values => {
    const { user, hasCustomTopic, customTopic, ...requestData } = values;
    const payload = { ...requestData };

    if (hasCustomTopic) {
      const { data } = customTopic?.id
        ? await http.patch(api_one_on_one_topic(customTopic?.id), {
            name: customTopic?.name,
          })
        : await http.post(API_ONE_ON_ONE_TOPICS, {
            name: customTopic?.name,
          });

      if (data?.id) {
        payload.agenda = [...payload.agenda, data.id];
      }
    }

    await http.patch(api_one_on_one_record(oneOnOneRequest.id), {
      ...payload,
      user: user.id,
    });

    onCloseRequest();
    showSuccessMessage(translations.oneOnOne.successMessages.meetingCreated);

    if (activeOneOnOneTab.id !== myOneOnOneTab.id) {
      dispatch(setLoaders({ oneOnOneLoader: true }));
      dispatch(clearOneOnOne());
      setOneOnOnePage(DASHBOARD_DEFAULT_PARAMS.page);

      try {
        await getOneOnOne(dispatch, activeOneOnOneTab.url);
      } finally {
        dispatch(setLoaders({ oneOnOneLoader: false }));
      }
    }

    return getMyToDo(dispatch);
  };

  const onToggleAbsenceRequestAcceptDialog = () =>
    setIsAcceptAbsenceRequestOpened(prevState => !prevState);
  const onToggleAbsenceRequestRejectDialog = () =>
    setIsRejectAbsenceRequestOpened(prevState => !prevState);

  const onAcceptAbsenceRequest = absenceId => {
    onToggleAbsenceRequestAcceptDialog();
    setAbsenceRequestId(absenceId);
  };

  const onDismissAbsenceRequest = absenceId => {
    onToggleAbsenceRequestRejectDialog();
    setAbsenceRequestId(absenceId);
  };

  const onAcceptAbsence = async () => {
    try {
      await http.patch(api_absence_request_status(absenceRequestId), {
        [PARAMS.STATUS]: ABSENCE_REQUEST_STATUS_TYPES.APPROVED,
      });
      onToggleAbsenceRequestAcceptDialog();
      showSuccessMessage(
        translations.absenceRequest.approveAbsenceRequestDialog.acceptSuccess
      );
      getMyToDo(dispatch);
    } catch {
      onToggleAbsenceRequestAcceptDialog();
    }
  };

  const onRejectAbsence = async () => {
    try {
      await http.patch(api_absence_request_status(absenceRequestId), {
        [PARAMS.STATUS]: ABSENCE_REQUEST_STATUS_TYPES.REJECTED,
      });
      onToggleAbsenceRequestRejectDialog();
      showSuccessMessage(
        translations.absenceRequest.rejectAbsenceRequestDialog.rejectSuccess
      );
      getMyToDo(dispatch);
    } catch {
      onToggleAbsenceRequestRejectDialog();
    }
  };

  const onOneOnOneTabSelect = selectedOneOnOne => async () => {
    if (selectedOneOnOne.id !== activeOneOnOneTab.id) {
      dispatch(setLoaders({ oneOnOneLoader: true }));
      dispatch(clearOneOnOne());
      if (activeOneOnOneTab.id === myOneOnOneTab.id) {
        dispatch(clearMyOneOnOneRequest());
      }
      setActiveOneOnOneTab(selectedOneOnOne);
      setOneOnOnePage(DASHBOARD_DEFAULT_PARAMS.page);
      try {
        await Promise.all([
          getOneOnOne(dispatch, selectedOneOnOne.url),
          ...(selectedOneOnOne.id === myOneOnOneTab.id
            ? [getMyOneOnOneRequest(dispatch)]
            : []),
        ]);
      } finally {
        dispatch(setLoaders({ oneOnOneLoader: false }));
      }
    }
  };

  const onViewPlanDetails = planId =>
    navigate(`/action-plans/${planId}/details`);

  const onLoadMorePlans = () => {
    const page = plansPage + 1;

    setPlansPage(page);
    return getPlans(dispatch, activePlansTab.url, { page }, true);
  };

  const onOpenPlanProposal = () => {
    setIsPlanProposalOpened(true);
    setIsPlanProposa(true);
  };

  const onAcceptProposal = async proposalId => {
    const { data } = await http.get(api_action_plan(proposalId));

    setIsPlanProposalOpened(true);
    setPlanProposal(data);
  };

  const onClosePlanProposal = () => {
    setIsPlanProposalOpened(false);
    setIsPlanProposa(false);
    setPlanProposal({});
  };

  const onSavePlan = plan => {
    const hasTasksInPlan = plan?.tasks.some(task => task?.user === auth.id);
    if (isPlanProposal) {
      onClosePlanProposal();
      setPlansPage(DASHBOARD_DEFAULT_PARAMS.page);
      return getPlans(dispatch);
    }
    onClosePlanProposal();

    if (activePlansTab.id !== myActionPlansTab.id) {
      dispatch(setLoaders({ plansLoader: true }));
      dispatch(clearPlans());
      setPlansPage(DASHBOARD_DEFAULT_PARAMS.page);
      getPlans(dispatch, activePlansTab.url);
    }
    if (hasTasksInPlan) {
      getMyTasks(dispatch, {
        ...(shouldHideFinishedTask ? { is_finished: false } : {}),
      });
    }
    return getMyToDo(dispatch);
  };

  const onDismissProposal = proposal => {
    setPlanProposal(proposal);
    setIsDismissProposalOpened(true);
  };

  const onCloseDismissProposal = () => {
    setIsDismissProposalOpened(false);
    setPlanProposal({});
  };

  const onDeleteProposal = async () => {
    try {
      await http.delete(api_action_plan(planProposal.id));
      onCloseDismissProposal();
      showSuccessMessage(
        translations.plans.dismissActionPlanProposalDialog.deleteSuccess
      );
      getMyToDo(dispatch);
    } catch {
      onCloseDismissProposal();
    }
  };

  const onPlansTabSelect = selectedPlan => () => {
    if (selectedPlan.id !== activePlansTab.id) {
      dispatch(setLoaders({ plansLoader: true }));
      dispatch(clearPlans());
      setActivePlansTab(selectedPlan);
      setPlansPage(DASHBOARD_DEFAULT_PARAMS.page);
      getPlans(dispatch, selectedPlan.url);
    }
  };

  const onLoadMoreTasks = () => {
    const page = myTasksPage + 1;

    setMyTasksPage(page);
    return getMyTasks(
      dispatch,
      { page, ...(shouldHideFinishedTask ? { is_finished: false } : {}) },
      true
    );
  };

  const onCloseTaskStatus = () => setSelectedTask(null);

  const onTaskStatusChange = task => setSelectedTask(task);

  const onConfirmTaskStatus = async () => {
    await changeTaskStatus(dispatch, selectedTask.id);
    if (!selectedTask.is_finished && shouldHideFinishedTask) {
      setMyTasksPage(DASHBOARD_DEFAULT_PARAMS.page);
      getMyTasks(dispatch, {
        is_finished: false,
      });
    }
    onCloseTaskStatus();
    showSuccessMessage(translations.tasks.taskStatusDialog.success);
  };

  const onToggleTasksVisibility = async isChecked => {
    if (isObjectEmpty(tasksPreset)) {
      await addNewPreset(dispatch, {
        name: DASHBOARD_TASKS_PRESET,
        location: PRESETS.DASHBOARD,
        data: { shouldHideFinishedTask: isChecked },
      });
    } else {
      await onEditPreset(dispatch, {
        id: tasksPreset.id,
        name: DASHBOARD_TASKS_PRESET,
        data: { shouldHideFinishedTask: isChecked },
      });
    }
    setMyTasksPage(DASHBOARD_DEFAULT_PARAMS.page);
    setIsTasksVisibilityLoading(true);
    await getMyTasks(dispatch, {
      ...(isChecked ? { is_finished: !isChecked } : {}),
    });
    setIsTasksVisibilityLoading(false);
    setShouldHideFinishedTasks(isChecked);
  };

  const onCreateTag = async tagName => {
    const tags = await onSaveCreatableTag(tagName);
    const [tag] = tags.create;

    dispatch(addMultipleTags({ categoryId: GENERAL_CATEGORY_ID, tags }));

    return tag;
  };

  const onFillSurvey = async surveyId => {
    const { data } = await http.get(api_get_survey_tracking_by_id(surveyId));
    const trackingToken =
      data && data.tracking_token ? data.tracking_token : '';

    if (trackingToken) {
      dispatch(setIsInlineSurvey(true));

      navigate(`/fill-survey/${trackingToken}`);
    }
  };

  const onLoadNotifications = async () => {
    if (notificationCount > 0) {
      return getNotifications(
        dispatch,
        canAccessUserProfileCheck,
        canAccessDetailsCheck
      );
    }

    const { payload: count } = await getNotificationsCount(dispatch);

    if (!count) return { data: [] };

    return getNotifications(
      dispatch,
      canAccessUserProfileCheck,
      canAccessDetailsCheck
    );
  };

  const onMarkNotificationAsRead = notificationId =>
    onClearNotification(dispatch, notificationId);

  const handleClearAllNotifications = () => onClearAllNotifications(dispatch);

  const onCloseNotifications = () => dispatch(clearNotificationItems());

  const onViewNotification = notificationId => {
    const notification = getItemById(notificationItems, notificationId);

    if (!notification) return null;

    onClearNotification(dispatch, notification.id);

    if (!notification.route) return null;

    const shouldPassNotificationState =
      !isArrayEmpty(notification.updates) ||
      notification.model === NOTIFICATION_MODEL_KEYS.DOCUMENT;

    return navigate(notification.route, {
      ...(shouldPassNotificationState
        ? {
            state: {
              notifications: {
                objectId: notification.object_id,
                isUpdate: notification.action === NOTIFICATION_ACTIONS.UPDATE,
                model: notification.model,
                updates: notification.updates,
              },
            },
          }
        : {}),
    });
  };

  const onTeamSelect = selectedTeam => () => {
    if (selectedTeam.id !== activeTeam.id) {
      dispatch(setLoaders({ teamStatsLoader: true }));
      dispatch(clearTeamStats());
      setActiveTeam(selectedTeam);
      return getTeamStats(dispatch, getObjectToNumberArray(selectedTeam.users));
    }
  };

  const onOpenManageTeam = () => setIsManageTeamOpened(true);

  const onOpenEditTeam = currentTeam => () => {
    setIsManageTeamOpened(true);
    setTeam(currentTeam);
  };

  const onCloseManageTeam = () => {
    setIsManageTeamOpened(false);
    setTeam(INITIAL_TEAM_DATA);
  };

  const onSaveTeam = async ({ users, name }) => {
    const data = {
      users,
    };

    if (team.id) {
      await onEditPreset(dispatch, {
        id: team.id,
        name: trimString(name),
        data,
      });

      if (
        team.users.length !== users.length ||
        getUnionOfTwoArrays(users, team.users).length !== users.length
      ) {
        dispatch(setLoaders({ teamStatsLoader: true }));
        dispatch(clearTeamStats());
        getTeamStats(dispatch, getObjectToNumberArray(users));
      }

      setActiveTeam(prevActiveTeam => ({
        ...prevActiveTeam,
        name: trimString(name),
        users,
        count: users.length,
      }));

      return Promise.resolve();
    }

    const teamData = await addNewPreset(dispatch, {
      name: trimString(name),
      location: PRESETS.DASHBOARD,
      data,
    });

    const newActiveTeam = {
      ...teamData,
      id: teamData.id,
      count: teamData.data.users.length,
      users: teamData.data.users,
      isEditable: true,
    };

    dispatch(setLoaders({ teamStatsLoader: true }));
    dispatch(clearTeamStats());
    setActiveTeam(newActiveTeam);
    getTeamStats(dispatch, getObjectToNumberArray(newActiveTeam.users));

    showSuccessMessage(
      translations.myTeam.manageTeam.successMessages.addPreset
    );

    return Promise.resolve();
  };

  const onOpenDeleteTeam = () => setIsDeleteTeamOpened(true);

  const onCloseDeleteTeam = () => setIsDeleteTeamOpened(false);

  const onDeleteTeam = async () => {
    try {
      await deleteSelectedPreset(dispatch, activeTeam.id, activeTeam.location);
      const teamIndex = getItemIndex(teams, activeTeam.id);
      let newActiveTeam = null;

      if (teams.length !== 1) {
        if (teamIndex === 0) {
          const [, nextTeam] = teams;
          newActiveTeam = { ...nextTeam };
        } else {
          newActiveTeam = { ...teams[teamIndex - 1] };
        }

        dispatch(setLoaders({ teamStatsLoader: true }));
        dispatch(clearTeamStats());
        setActiveTeam(newActiveTeam);
        getTeamStats(dispatch, getObjectToNumberArray(newActiveTeam.users));
      } else {
        dispatch(clearTeamStats());
        setActiveTeam(null);
      }
      showSuccessMessage(translations.myTeam.deleteTeamDialog.deleteSuccess);
    } finally {
      onCloseDeleteTeam();
      onCloseManageTeam();
    }
  };

  const onOpenViewTeam = () => setIsViewTeamOpened(true);

  const onCloseViewTeam = () => setIsViewTeamOpened(false);

  const teamMenuItems = prepareMyTeamMenuItems(
    translations.myTeam.menuItems,
    onOpenEditTeam,
    onOpenDeleteTeam,
    activeTeam
  );

  return (
    <PageContainer
      {...rest}
      translations={translations}
      auth={auth}
      accessControl={accessControl}
      navigate={navigate}
      organizationSettings={organizationSettings}
      shouldPassProps={false}
      isFullWidthContent
      headerProps={{
        notifications: (
          <GlobalNotifications
            translations={translations.globalNotifications}
            notificationCount={notificationCount}
            notificationItems={notificationItems}
            canAccessNotificationCheck={canAccessNotificationCheck}
            onLoadNotifications={onLoadNotifications}
            onCloseNotifications={onCloseNotifications}
            onClearAllNotifications={handleClearAllNotifications}
            onMarkAsRead={onMarkNotificationAsRead}
            onViewNotification={onViewNotification}
          />
        ),
      }}
    >
      <div className={classes.root}>
        <div className={classes.twoColumns}>
          <DashboardSection
            translations={translations.toDo}
            isHeaderLoading={isIntialLoad}
            isContentLoading={myToDoLoader}
            color={FEATURE_COLORS.TO_DO}
            itemPropKey="data"
            itemComponent={ToDoCard}
            items={prepareToDoItems(
              myToDo,
              surveyTypes,
              canAccessUserProfileCheck
            )}
            isDoubleApprove={organizationSettings.absence_double_approval}
            sectionIcon={CleanIcon}
            onFillSurvey={onFillSurvey}
            onAcceptProposal={onAcceptProposal}
            onDismissProposal={onDismissProposal}
            onAcceptRequest={onAcceptRequest}
            onDismissRequest={onOpenDismissRequest}
            onAcceptAbsenceRequest={onAcceptAbsenceRequest}
            onDismissAbsenceRequest={onDismissAbsenceRequest}
          />
        </div>
        <div
          className={classNames(classes.tasksSection, {
            [classes.taskSectionOneRow]:
              !canViewOneOnOne && !canViewActionPlans && !canViewCourses,
          })}
        >
          <DashboardSection
            translations={{
              ...translations.tasks,
              noData: {
                ...(isTasksVisibilittLoading
                  ? {}
                  : {
                      ...(shouldHideFinishedTask
                        ? { ...translations.tasks.noDataFinished }
                        : { ...translations.tasks.noData }),
                    }),
              },
            }}
            isHeaderLoading={isIntialLoad}
            isContentLoading={myTasksLoader}
            isNotificationDisabled={isTasksVisibilittLoading}
            color={FEATURE_COLORS.TASKS}
            itemPropKey="task"
            itemComponent={TaskCard}
            items={myTasks?.results}
            sectionIcon={TasksIcon}
            currentUser={auth}
            actionButtons={[
              ...(hasTaskOnLoad
                ? [
                    {
                      id: 'tasksSwitch',
                      className: classes.switch,
                      button: CustomSwitch,
                      tooltipText: translations.tasks.statusTooltip,
                      isChecked: shouldHideFinishedTask,
                      onChange: onToggleTasksVisibility,
                    },
                  ]
                : []),
            ]}
            hasLoadMore={hasNextPage(myTasks)}
            onLoadMoreItems={onLoadMoreTasks}
            onChange={onTaskStatusChange}
            isVertical
          />
        </div>
        {canViewOneOnOne && (
          <div className={classes.twoColumns}>
            <DashboardSection
              translations={translations.oneOnOne}
              isHeaderLoading={isIntialLoad}
              isContentLoading={oneOnOneLoader}
              color={FEATURE_COLORS.ONE_ON_ONE}
              itemPropKey="oneOnOne"
              itemComponent={OneOnOneMiniCard}
              items={prepareOneOnOneRecordsData(
                [
                  ...(hasOneOnOneRequest
                    ? [{ ...myOnOneRequest, isPendingRequest: true }]
                    : []),
                  ...oneOnOne.results,
                ],
                auth,
                canAccessUserProfileCheck,
                canAccessDetailsCheck
              )}
              tabs={oneOnOneTabs}
              activeTab={activeOneOnOneTab}
              actionButtons={[
                ...(hasOneOnOneRequestButton
                  ? [
                      {
                        id: 'request',
                        className: classes.actionButton,
                        classes: {
                          startIcon: classNames(classes.actionButtonIcon, {
                            [classes.actionButtonIconDisabled]:
                              hasOneOnOneRequest,
                          }),
                        },
                        button: CustomButton,
                        type: 'withTextLightRounded',
                        startIcon: <RequestIcon />,
                        text: translations.oneOnOne.requestButton,
                        disabled: hasOneOnOneRequest,
                        onClick: onAddOneOnOneRequest,
                      },
                    ]
                  : []),
              ]}
              sectionIcon={OneOnOneIcon}
              myManager={mySuperior}
              currentPage={oneOnOnePage}
              hasLoadMore={hasNextPage(oneOnOne)}
              onTabSelect={onOneOnOneTabSelect}
              onDeleteRequest={onDeleteRequestOpen}
              onEditRequest={onOpenOneOnOneRequest}
              onLoadMoreItems={onLoadMoreOneOnOne}
              onCardClick={onViewOneOnOneDetails}
              hasTabs
            />
          </div>
        )}
        {canViewActionPlans && (
          <div
            className={classNames({
              [classes.twoColumns]:
                (users_see_courses && canViewCourses) || !canViewOneOnOne,
              [classes.fullRow]:
                (!users_see_courses || !canViewCourses) && canViewOneOnOne,
            })}
          >
            <DashboardSection
              translations={translations.plans}
              isHeaderLoading={isIntialLoad}
              isContentLoading={plansLoader}
              color={FEATURE_COLORS.PLANS}
              itemPropKey="plan"
              itemComponent={PlanMiniCard}
              items={preparePlansData(
                plans.results,
                auth,
                planStatuses,
                canAccessUserProfileCheck,
                canAccessDetailsCheck
              )}
              tabs={actionPlanTabs}
              activeTab={activePlansTab}
              actionButtons={
                hasActionPlanProposeButton
                  ? [
                      {
                        id: 'propose',
                        className: classes.actionButton,
                        classes: { startIcon: classes.actionButtonIcon },
                        button: CustomButton,
                        type: 'withTextLightRounded',
                        startIcon: <RequestIcon />,
                        text: translations.plans.proposeButton,
                        onClick: onOpenPlanProposal,
                      },
                    ]
                  : []
              }
              sectionIcon={PlansIcon}
              currentPage={plansPage}
              hasLoadMore={hasNextPage(plans)}
              onTabSelect={onPlansTabSelect}
              onCardClick={onViewPlanDetails}
              onLoadMoreItems={onLoadMorePlans}
              canSeeCourses={users_see_courses}
              hasTabs
            />
          </div>
        )}
        {users_see_courses && canViewCourses && (
          <div
            className={classNames({
              [classes.twoColumns]: !canViewActionPlans && !canViewOneOnOne,
              [classes.fullRow]:
                (canViewActionPlans && !canViewOneOnOne) ||
                (canViewOneOnOne && !canViewActionPlans),
            })}
          >
            <DashboardSection
              translations={translations.courses}
              isHeaderLoading={isIntialLoad}
              isContentLoading={myCoursesLoader}
              color={FEATURE_COLORS.COURSES}
              itemPropKey="course"
              itemComponent={CourseMiniCard}
              items={
                hasCourses
                  ? prepareCourses(myCourses.results, courseStatuses)
                  : []
              }
              statuses={courseStatuses}
              sectionIcon={CoursesIcon}
              shouldFade={!myCoursesLoader && hasCourses}
              hasLoadMore={hasNextPage(myCourses)}
              onStatusChange={onCourseStatusChange}
              onCardClick={onViewCourseDetails}
              onLoadMoreItems={onLoadMoreCourses}
            />
          </div>
        )}
        <div className={classes.fullRow}>
          <DashboardSection
            className={classNames(classes.myTeam, {
              [classes.myTeamNoCourses]: !users_see_courses || !canViewCourses,
            })}
            headerClass={classes.myTeamHeader}
            contentClass={classes.myTeamContent}
            scrollWrapperClass={classes.scrollWrapperClass}
            scrollArrowClass={classNames(classes.myTeamScrollArrow, {
              [classes.myTeamScrollArrowNoCourses]:
                !users_see_courses || !canViewCourses,
            })}
            noResultsClass={classes.myTeamNoResults}
            translations={translations.myTeam}
            tabs={teams}
            activeTab={activeTeam}
            isHeaderLoading={isIntialLoad}
            isContentLoading={teamStatsLoader}
            color={FEATURE_COLORS.TEAM}
            itemPropKey="user"
            itemComponent={TeamUserStatsCard}
            items={teamStats}
            sectionIcon={MyTeamIcon}
            actionButtons={[
              ...(!isArrayEmpty(teamsAvailableUsers)
                ? [
                    {
                      id: 'addTeam',
                      className: classes.addTeamButton,
                      iconButtonClass: classes.addTeamIconButton,
                      button: ActionButton,
                      type: ACTION_BUTTON_TYPES.ADD,
                      tooltipText: translations.myTeam.addTeam,
                      hasBorder: true,
                      onClickHandler: onOpenManageTeam,
                    },
                  ]
                : []),
            ]}
            subHeaderActionButtons={[
              ...(activeTeam
                ? [
                    {
                      id: 'viewAll',
                      button: CustomButton,
                      className: classes.actionButton,
                      type: 'withTextLightRounded',
                      text: translations.myTeam.viewAll,
                      onClick: onOpenViewTeam,
                    },
                  ]
                : []),
              ...(activeTeam && activeTeam.isEditable
                ? [
                    {
                      id: 'menu',
                      button: DotsMenu,
                      className: classes.menu,
                      menuItems: teamMenuItems,
                    },
                  ]
                : []),
            ]}
            courseStatuses={courseStatuses}
            onTabSelect={onTeamSelect}
            canAccessUserProfileCheck={canAccessUserProfileCheck}
            canAccessProfileSectionCheck={canAccessDetailsCheck}
            onGoToUserProfile={onGoToUserProfile}
            canViewCourses={users_see_courses && canViewCourses}
            hasSubHeader
            hasTabs
          />
        </div>
        <CustomFormDrawer
          translations={prepareUserTopicSidebarTranslations(
            translations.oneOnOne.manageTopicForm,
            !!employeeTopic?.name,
            true
          )}
          isOpened={isEmployeeTopicOpened}
          isInitialValid={!!employeeTopic?.id}
          initialData={employeeTopic}
          fields={prepareUserTopicFields()}
          onClose={onCloseOneOnOneRequest}
          onSave={onSaveOneOnOneRequest}
          hideDelete
        />
        <ManageActionPlan
          translations={translations.plans.manageActionPlan}
          isOpen={isPlanProposalOpened}
          statuses={planStatuses}
          user={isPlanProposal ? auth : {}}
          allEmployees={availableUsers}
          managableEmployees={managableUsers}
          categories={categories}
          courses={courses.results}
          canSeeCourses={users_see_courses}
          isPlanProposal={isPlanProposal}
          actionPlan={planProposal}
          auth={auth}
          accessControl={accessControl}
          isInitialValid={!isPlanProposal}
          getCourses={onGetCourses}
          getCourseEnrolledUsers={onGetCourseEnrolledUsers}
          onCoursesSearch={onCoursesSearch}
          onCreateTag={onCreateTag}
          onClose={onClosePlanProposal}
          onConfirm={onSavePlan}
          hideDelete
        />
        <CustomFormDrawer
          customContentClass={classes.drawerContent}
          translations={translations.myTeam.manageTeam}
          isOpened={isManageTeamOpened}
          isInitialValid={!!team?.id}
          initialData={team}
          fields={MANAGE_TEAM_FIELDS}
          allUsers={teamsAvailableUsers}
          onDelete={onOpenDeleteTeam}
          onClose={onCloseManageTeam}
          onSave={onSaveTeam}
          hasCancelButton
        />
        <ManageOneOnOne
          translations={translations.oneOnOne.oneOnOneRecordDialog}
          isOpened={isAcceptRequestOpened}
          topics={topics}
          record={
            isObject(oneOnOneRequest) && !isObjectEmpty(oneOnOneRequest)
              ? oneOnOneRequest
              : {}
          }
          createdFor={oneOnOneRequest?.user}
          auth={auth}
          accessControl={accessControl}
          categories={categories}
          onCreateTag={onCreateTag}
          onClose={onCloseRequest}
          onSave={onSaveAcceptedRequest}
          isOneOnOneRequest
        />
        <PreviewTeamDialog
          translations={translations.myTeam.previewTeamDialog}
          isOpened={isViewTeamOpened}
          title={`${translations.myTeam.view} ${activeTeam?.name} (${activeTeam?.count})`}
          team={teamStats}
          courseStatuses={courseStatuses}
          hasCourses={users_see_courses && canViewCourses}
          canAccessUserProfileCheck={canAccessUserProfileCheck}
          canAccessProfileSectionCheck={canAccessDetailsCheck}
          onGoToUserProfile={onGoToUserProfile}
          onClose={onCloseViewTeam}
        />
        <AlertDialog
          translations={{
            ...translations.tasks.taskStatusDialog,
            contentMessage: selectedTask?.is_finished
              ? translations.tasks.taskStatusDialog.contentMessageUncheck
              : translations.tasks.taskStatusDialog.contentMessageCheck,
          }}
          isOpened={!!selectedTask}
          onClose={onCloseTaskStatus}
          onConfirm={onConfirmTaskStatus}
        />
        <AlertDialog
          translations={translations.oneOnOne.upcomingMeetingDialog}
          isOpened={hasUpcomingOneOnOne}
          onClose={onToggleUpcomingOneOnOneDialog}
          onConfirm={onConfirmUpcomingMeetingDialog}
        />
        <AlertDialog
          translations={translations.absenceRequest.approveAbsenceRequestDialog}
          isOpened={isAcceptAbsenceRequestOpened}
          onClose={onToggleAbsenceRequestAcceptDialog}
          onConfirm={onAcceptAbsence}
        />
        <AlertDialog
          translations={translations.oneOnOne.deleteMeetingRequestDialog}
          isOpened={isDeleteRequestOpened}
          onClose={onCloseDeleteRequestDialog}
          onConfirm={onDeleteRequest}
          isWarning
        />
        <AlertDialog
          translations={translations.plans.dismissActionPlanProposalDialog}
          isOpened={isDismissProposalOpened}
          onClose={onCloseDismissProposal}
          onConfirm={onDeleteProposal}
          isWarning
        />
        <AlertDialog
          translations={translations.oneOnOne.dismissOneOnOneRequestDialog}
          isOpened={isDismissRequestOpened}
          onClose={onCloseDismissRequest}
          onConfirm={onDismissRequest}
          isWarning
        />
        <AlertDialog
          translations={translations.myTeam.deleteTeamDialog}
          isOpened={isDeleteTeamOpened}
          onClose={onCloseDeleteTeam}
          onConfirm={onDeleteTeam}
          isWarning
        />
        <AlertDialog
          translations={translations.absenceRequest.rejectAbsenceRequestDialog}
          isOpened={isRejectAbsenceRequestOpened}
          onClose={onToggleAbsenceRequestRejectDialog}
          onConfirm={onRejectAbsence}
          isWarning
        />
      </div>
    </PageContainer>
  );
};

DashboardPage.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  presets: PropTypes.object,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(DashboardPage);
